/**
 * ModulesTab — Active + excluded module IDs.
 *
 * Displays modules as tag chips grouped by category (M / E / V / C / A).
 * Add/remove via the input at the bottom; the zod regex enforces the
 * Module Catalog BÖLÜM 5 naming pattern.
 */
import { Check, Plus, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button';
import { Card, CardBody, CardFooter, CardHeader } from '../../../shared/ui/Card';
import { FieldError } from '../../../shared/ui/FieldError';
import { Input } from '../../../shared/ui/Input';
import { Label } from '../../../shared/ui/Label';
import { cn } from '../../../shared/utils/cn';
import { useUpdateBusinessProfile } from '../../../features/business-profiles/useBusinessProfiles';
import {
  ModuleIdSchema,
  type BusinessProfileRecord,
} from '../../../features/business-profiles/types';

type Bucket = 'M' | 'E' | 'V' | 'C' | 'A';

function bucketOf(id: string): Bucket {
  const first = id.charAt(0) as Bucket;
  return (['M', 'E', 'V', 'C', 'A'] as const).includes(first) ? first : 'M';
}

const BUCKET_ORDER: Bucket[] = ['M', 'E', 'V', 'C', 'A'];

export function ModulesTab({ record }: { record: BusinessProfileRecord }): JSX.Element {
  const { t } = useTranslation('adminProfiles');
  const updateMutation = useUpdateBusinessProfile(record.subVerticalKey);

  const [modules, setModules] = useState<string[]>(record.profile.modules);
  const [excluded, setExcluded] = useState<string[]>(record.profile.excludedModules ?? []);
  const [newModule, setNewModule] = useState('');
  const [target, setTarget] = useState<'active' | 'excluded'>('active');
  const [addError, setAddError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    setModules(record.profile.modules);
    setExcluded(record.profile.excludedModules ?? []);
  }, [record.id, record.profile.modules, record.profile.excludedModules]);

  const isDirty =
    JSON.stringify([...modules].sort()) !==
      JSON.stringify([...record.profile.modules].sort()) ||
    JSON.stringify([...excluded].sort()) !==
      JSON.stringify([...(record.profile.excludedModules ?? [])].sort());

  const handleAdd = () => {
    setAddError(null);
    const normalized = newModule.trim().toUpperCase();
    if (!normalized) return;
    const parsed = ModuleIdSchema.safeParse(normalized);
    if (!parsed.success) {
      setAddError(t('modules.invalidFormat'));
      return;
    }
    const list = target === 'active' ? modules : excluded;
    if (list.includes(normalized)) {
      setAddError(t('modules.duplicate'));
      return;
    }
    if (target === 'active') {
      setModules([...modules, normalized]);
      setExcluded(excluded.filter((m) => m !== normalized));
    } else {
      setExcluded([...excluded, normalized]);
      setModules(modules.filter((m) => m !== normalized));
    }
    setNewModule('');
  };

  const handleRemove = (id: string, from: 'active' | 'excluded') => {
    if (from === 'active') {
      setModules(modules.filter((m) => m !== id));
    } else {
      setExcluded(excluded.filter((m) => m !== id));
    }
  };

  const onSave = async () => {
    setJustSaved(false);
    await updateMutation.mutateAsync({
      modules,
      excludedModules: excluded.length > 0 ? excluded : undefined,
    });
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 2500);
  };

  const onReset = () => {
    setModules(record.profile.modules);
    setExcluded(record.profile.excludedModules ?? []);
    setAddError(null);
    setNewModule('');
  };

  const renderGroup = (
    list: string[],
    kind: 'active' | 'excluded',
  ): JSX.Element => {
    const grouped: Record<Bucket, string[]> = { M: [], E: [], V: [], C: [], A: [] };
    list.forEach((id) => grouped[bucketOf(id)].push(id));
    return (
      <div className="space-y-biz-content-lg">
        {BUCKET_ORDER.map((bucket) => {
          const items = grouped[bucket];
          if (items.length === 0) return null;
          return (
            <div key={bucket}>
              <h4 className="text-biz-xs font-semibold uppercase tracking-wider text-biz-muted-foreground mb-biz-content">
                {t(`modules.buckets.${bucket}` as const)}
              </h4>
              <ul className="flex flex-wrap gap-biz-content">
                {items.map((id) => (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => handleRemove(id, kind)}
                      aria-label={`${id} — ${t('modules.removeLabel')}`}
                      className={cn(
                        'group inline-flex items-center gap-biz-content',
                        'px-biz-content-lg py-biz-content min-h-[44px]',
                        'rounded-biz-full border font-mono text-biz-sm',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
                        'transition-colors duration-biz-fast',
                        kind === 'active'
                          ? 'bg-biz-primary-subtle text-biz-primary border-biz-primary/30 hover:bg-biz-primary/10'
                          : 'bg-biz-warning-subtle text-biz-warning border-biz-warning/30 hover:bg-biz-warning/10',
                      )}
                    >
                      <span>{id}</span>
                      <X
                        size={14}
                        weight="bold"
                        aria-hidden="true"
                        className="opacity-60 group-hover:opacity-100"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <h2 className="text-biz-lg biz-heading font-semibold">{t('modules.title')}</h2>
          <p className="text-biz-sm text-biz-muted-foreground mt-biz-content">
            {t('modules.hint')}
          </p>
        </div>
      </CardHeader>
      <CardBody className="space-y-biz-section">
        <section>
          <h3 className="text-biz-base font-semibold mb-biz-content-lg">
            {t('modules.activeTitle')}{' '}
            <span className="text-biz-sm text-biz-muted-foreground font-normal">
              ({modules.length})
            </span>
          </h3>
          {modules.length === 0 ? (
            <p className="text-biz-sm text-biz-muted-foreground">{t('modules.empty')}</p>
          ) : (
            renderGroup(modules, 'active')
          )}
        </section>

        <section>
          <h3 className="text-biz-base font-semibold mb-biz-content-lg">
            {t('modules.excludedTitle')}{' '}
            <span className="text-biz-sm text-biz-muted-foreground font-normal">
              ({excluded.length})
            </span>
          </h3>
          {excluded.length === 0 ? (
            <p className="text-biz-sm text-biz-muted-foreground">
              {t('modules.excludedEmpty')}
            </p>
          ) : (
            renderGroup(excluded, 'excluded')
          )}
        </section>

        <section className="pt-biz-content-lg border-t border-biz-border">
          <Label htmlFor="new-module">{t('modules.addLabel')}</Label>
          <div className="flex gap-biz-content-lg flex-col md:flex-row mt-biz-content">
            <Input
              id="new-module"
              placeholder="M01 / V-GRM-01"
              value={newModule}
              onChange={(e) => setNewModule(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              invalid={Boolean(addError)}
              className="md:flex-1"
            />
            <select
              aria-label={t('modules.addTargetLabel')}
              className="min-h-biz-touch px-biz-content-lg py-biz-content bg-biz-background text-biz-foreground border border-biz-input rounded-biz-input"
              value={target}
              onChange={(e) => setTarget(e.target.value as 'active' | 'excluded')}
            >
              <option value="active">{t('modules.activeTarget')}</option>
              <option value="excluded">{t('modules.excludedTarget')}</option>
            </select>
            <Button
              type="button"
              intent="secondary"
              leftIcon={<Plus size={18} weight="regular" />}
              onClick={handleAdd}
            >
              {t('modules.addCta')}
            </Button>
          </div>
          <FieldError>{addError}</FieldError>
          <p className="mt-biz-content text-biz-xs text-biz-muted-foreground">
            {t('modules.addHint')}
          </p>
        </section>
      </CardBody>
      <CardFooter>
        {justSaved ? (
          <span
            role="status"
            className="inline-flex items-center gap-1.5 text-biz-sm text-biz-success mr-auto"
          >
            <Check weight="regular" size={18} aria-hidden="true" />
            {t('common.saved')}
          </span>
        ) : null}
        <Button
          type="button"
          intent="secondary"
          onClick={onReset}
          disabled={!isDirty || updateMutation.isPending}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          intent="primary"
          loading={updateMutation.isPending}
          disabled={!isDirty}
          onClick={onSave}
        >
          {updateMutation.isPending ? t('common.saving') : t('common.save')}
        </Button>
      </CardFooter>
    </Card>
  );
}
